import { CreateTranscript, CreateTranscriptYear, Transcript } from '../swagger'

export type SourceYear = NonNullable<Transcript['transcriptYear1']>
export type SourceCourseWork = NonNullable<
  SourceYear['transcriptYearCourseWork']
>[number]
export type TargetCourseWork = NonNullable<
  CreateTranscriptYear['transcriptYearCourseWork']
>[number]

export function toCreateTranscriptYear(
  yearDataInput: Transcript['transcriptYear1'] | undefined
): CreateTranscriptYear | undefined {
  if (!yearDataInput) return undefined

  const { transcriptYearCourseWork = [], ...restAfterCourseWork } =
    yearDataInput as SourceYear
  const { transcriptYearKey, ...yearFields } = restAfterCourseWork
  void transcriptYearKey

  const cleanedCourseWorkList: TargetCourseWork[] = (
    transcriptYearCourseWork as SourceCourseWork[]
  ).map((courseWorkItem) => {
    const { transcriptYearCourseWorkKey, ...courseFields } = courseWorkItem
    void transcriptYearCourseWorkKey
    return courseFields as unknown as TargetCourseWork
  })

  return {
    ...(yearFields as unknown as Omit<
      CreateTranscriptYear,
      'transcriptYearCourseWork'
    >),
    transcriptYearCourseWork: cleanedCourseWorkList,
  }
}

export function toDateOrUndefined(input: unknown): Date | undefined {
  if (!input) return undefined
  if (input instanceof Date) return input
  if (typeof input === 'string' || typeof input === 'number') {
    const parsed = new Date(input as string)
    return isNaN(parsed.getTime()) ? undefined : parsed
  }
  return undefined
}

export function cloneTranscript(
  originalTranscript: Transcript,
  duplicatedName: string
): CreateTranscript {
  const optional = <K extends keyof CreateTranscript>(
    key: K,
    value: CreateTranscript[K]
  ) =>
    value !== undefined && value !== null && value !== ''
      ? { [key]: value }
      : {}

  const studentBirthDate = toDateOrUndefined(
    originalTranscript.studentDateOfBirth
  )
  const highSchoolEntryDate = toDateOrUndefined(
    originalTranscript.dateHighSchoolEntry
  )
  const graduationDate = toDateOrUndefined(originalTranscript.dateGraduation)

  const dateFields = {
    studentDateOfBirth: studentBirthDate,
    dateHighSchoolEntry: highSchoolEntryDate,
    dateGraduation: graduationDate,
  }

  return {
    transcriptName: duplicatedName.trim(),
    studentKey: originalTranscript.student.studentKey,
    lastName: (originalTranscript.lastName ?? '').trim(),
    schoolName: (originalTranscript.schoolName ?? '').trim(),
    schoolAddress: originalTranscript.schoolAddress,

    ...optional('studentEmail', originalTranscript.studentEmail),
    ...optional(
      'studentAddressSameAsSchool',
      originalTranscript.studentAddressSameAsSchool
    ),
    ...optional('studentAddress', originalTranscript.studentAddress),
    ...optional(
      'gradingScaleKey',
      originalTranscript.gradingScale?.gradingScaleKey
    ),

    ...Object.fromEntries(Object.entries(dateFields).filter(([, v]) => v)),

    ...optional(
      'summary',
      typeof originalTranscript.summary === 'string'
        ? originalTranscript.summary.trim()
        : undefined
    ),
    ...optional('totalCredits', originalTranscript.totalCredits),
    ...optional('gpa', originalTranscript.gpa),

    ...optional(
      'transcriptYear1',
      toCreateTranscriptYear(originalTranscript.transcriptYear1)
    ),
    ...optional(
      'transcriptYear2',
      toCreateTranscriptYear(originalTranscript.transcriptYear2)
    ),
    ...optional(
      'transcriptYear3',
      toCreateTranscriptYear(originalTranscript.transcriptYear3)
    ),
    ...optional(
      'transcriptYear4',
      toCreateTranscriptYear(originalTranscript.transcriptYear4)
    ),
  }
}

export default cloneTranscript
